import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DesignPartnerDomainsService } from '../../../services/design-partner-domains.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-add-domain',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './add-domain.component.html',
  styleUrl: './add-domain.component.less',
})
export class AddDomainComponent {
  domain: string = '';
  isSubmitting: boolean = false;

  constructor(
    private domainsService: DesignPartnerDomainsService,
    private toaster: ToastService,
    private router: Router
  ) {}

  goBack(): void {
    this.router.navigate(['/domains']);
  }

  getDaysRemaining(notAfter: string): number {
    return Math.ceil((new Date(notAfter).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  }

  calculateRisk(cert: any, tls: any[]): string {
    const days = this.getDaysRemaining(cert.notAfter);
    const insecureTls = tls.some(
      (t: any) => (t.version === 'TLSv1' || t.version === 'TLSv1.1') && t.supported
    );
    if (days < 0 || days <= 14) return 'CRITICAL';
    if (days <= 30 || insecureTls) return 'HIGH';
    if (days <= 90) return 'MEDIUM';
    return 'LOW';
  }

  submitDomain(): void {
    const trimmed = this.domain.trim();
    if (!trimmed) {
      this.toaster.showToast('Please enter a domain name', 'error');
      return;
    }

    this.isSubmitting = true;

    this.domainsService.addDomain(trimmed).subscribe({
      next: () => {
        this.domainsService.searchCertificates(trimmed).subscribe({
          next: (response: any) => {
            const results: any[] = response?.results ?? [];

            // Deduplicate records by serialNumber
            const seen = new Set<string>();
            const uniqueRecords = results.filter((cert: any) => {
              if (seen.has(cert.serialNumber)) return false;
              seen.add(cert.serialNumber);
              return true;
            });

            // Fetch TLS for each unique record's commonName in parallel
            const tlsCalls = uniqueRecords.map(cert =>
              this.domainsService.getTlsInfo(cert.commonName).pipe(
                catchError(() => of({ domain: cert.commonName, tls: [] }))
              )
            );

            const tls$ = tlsCalls.length > 0 ? forkJoin(tlsCalls) : of([]);

            tls$.subscribe({
              next: (tlsResponses: any[]) => {
                // Build map: commonName -> tls versions array
                const tlsMap: { [key: string]: any[] } = {};
                tlsResponses.forEach((res: any, idx: number) => {
                  const key = uniqueRecords[idx]?.commonName;
                  if (key) tlsMap[key] = res?.tls ?? [];
                });

                // Augment unique records with tls + riskLevel for saving
                const toSave = uniqueRecords.map((cert: any) => {
                  const tls = tlsMap[cert.commonName] ?? [];
                  return { ...cert, tls, riskLevel: this.calculateRisk(cert, tls) };
                });

                this.domainsService.saveSubDomains(trimmed, toSave).subscribe({
                  next: () => {
                    this.isSubmitting = false;
                    this.toaster.showToast('Domain added successfully', 'success');
                    this.router.navigate(['/domains', trimmed]);
                  },
                  error: (err) => {
                    this.isSubmitting = false;
                    const message = err?.error?.message || 'Domain added but failed to save records';
                    this.toaster.showToast(message, 'error');
                    this.router.navigate(['/domains', trimmed]);
                  },
                });
              },
              error: () => {
                this.isSubmitting = false;
                this.toaster.showToast('Failed to fetch TLS information', 'error');
              },
            });
          },
          error: (err) => {
            this.isSubmitting = false;
            const message = err?.error?.message || 'Domain saved but failed to fetch certificates';
            this.toaster.showToast(message, 'error');
          },
        });
      },
      error: (err) => {
        this.isSubmitting = false;
        const message = err?.error?.message || err?.error?.error || 'Failed to add domain';
        this.toaster.showToast(message, 'error');
      },
    });
  }
}
